import { RETAILER_LABELS, isKept, type DropDetail, type DropOrder, type Retailer } from '@nebueye/shared'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useMemo, useState } from 'react'
import { ProductThumb, RetailerMark } from '@/components/app/retailer'
import { ErrorState } from '@/components/app/states'
import { StatusBadge } from '@/components/app/status-badge'
import {
  ColumnHeader,
  PanelHeader,
  Pips,
  RowChevron,
  StatBar,
  Surface,
  WentThroughLead,
  rowClass,
  rowEnter,
} from '@/components/app/surface'
import { Skeleton } from '@/components/ui/skeleton'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { ago, count, dateTime, money, moneyShort, percent, plural } from '@/lib/format'
import { dropQuery } from '@/lib/queries'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_app/drops/$id')({
  loader: ({ context, params }) => context.queryClient.prefetchQuery(dropQuery(params.id)),
  head: () => ({ meta: [{ title: 'Drop recap · Nebueye' }] }),
  component: DropRecap,
})

function DropRecap() {
  const { id } = Route.useParams()
  const q = useQuery(dropQuery(id))

  return (
    <div className="space-y-6">
      <Link to="/drops" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-sm">
        <ArrowLeft className="size-4" /> Drops
      </Link>
      {q.isError ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : !q.data ? (
        <div className="space-y-6" aria-busy="true" aria-label="Loading drop">
          <div className="flex gap-5">
            <Skeleton className="size-20 rounded-xl" />
            <div className="flex-1 space-y-3 pt-1">
              <Skeleton className="h-4 w-56" />
              <Skeleton className="h-8 w-96 max-w-full" />
            </div>
          </div>
          <Skeleton className="h-[104px] w-full rounded-xl" />
          <Skeleton className="h-80 w-full rounded-xl" />
        </div>
      ) : (
        <Recap data={q.data} />
      )}
    </div>
  )
}

function Recap({ data }: { data: DropDetail }) {
  const { drop, orders } = data
  const spent = orders.filter((o) => isKept(o.status)).reduce((s, o) => s + o.total, 0)
  const units = orders.filter((o) => isKept(o.status)).reduce((s, o) => s + o.quantity, 0)

  return (
    <>
      <header className="flex items-center gap-5">
        <ProductThumb src={drop.imageUrl} retailer={drop.retailer} className="size-20 rounded-xl text-lg" eager />
        <div className="min-w-0 space-y-1.5">
          <p className="text-muted-foreground flex flex-wrap items-center gap-x-1.5 text-sm">
            <RetailerMark retailer={drop.retailer} className="size-4 rounded text-[7px]" />
            {RETAILER_LABELS[drop.retailer]}
            <span className="text-subtle">
              · {dateTime(drop.dropStartedAt)} · {ago(drop.dropStartedAt)}
            </span>
          </p>
          <h1 className="text-headline font-semibold text-balance">{drop.productName}</h1>
        </div>
      </header>

      <StatBar
        lead={
          <WentThroughLead
            kept={drop.confirmedOrders}
            total={drop.totalOrders}
            meter={<Pips kept={drop.confirmedOrders} cancelled={drop.cancelledOrders} />}
          />
        }
        stats={[
          { label: 'Cancelled', value: count(drop.cancelledOrders), sub: `${percent(drop.cancelRate)} of orders` },
          { label: 'Shipped', value: count(drop.shippedOrders), sub: drop.notYetShipped ? `${count(drop.notYetShipped)} waiting to ship` : 'Nothing waiting' },
          { label: 'Delivered', value: count(drop.deliveredOrders), sub: drop.confirmedOrders ? `${percent(drop.deliveryRate)} of what went through` : 'Nothing to deliver' },
          { label: 'Spent', value: moneyShort(spent), sub: `${plural(units, 'unit')} kept` },
        ]}
      />

      <div className="grid items-start gap-6 lg:grid-cols-3">
        <Breakdown title="By card" rows={groupBy(orders, (o) => (o.cardType ? `${o.cardType}${o.cardLastFour ? ` ·· ${o.cardLastFour}` : ''}` : 'Card not stated'))} />
        <Breakdown
          title="By address"
          rows={groupBy(orders, (o) =>
            o.address ? [o.address.addressNumber, o.address.addressStreet].filter(Boolean).join(' ') || o.address.city || 'Address' : 'No address in email',
          )}
        />
        <CancelReasons orders={orders} />
      </div>

      <OrdersTable orders={orders} retailer={drop.retailer} />
    </>
  )
}

interface Group {
  label: string
  kept: number
  total: number
}

function groupBy(orders: DropOrder[], key: (o: DropOrder) => string): Group[] {
  const map = new Map<string, Group>()
  for (const o of orders) {
    const label = key(o)
    const g = map.get(label) ?? { label, kept: 0, total: 0 }
    g.total++
    if (isKept(o.status)) g.kept++
    map.set(label, g)
  }
  return [...map.values()].sort((a, b) => b.total - a.total || b.kept - a.kept).slice(0, 6)
}

/** How one dimension (card, address) split between went-through and cancelled. */
function Breakdown({ title, rows }: { title: string; rows: Group[] }) {
  return (
    <Surface>
      <PanelHeader title={title} />
      <ul className="space-y-3.5 px-5 pb-5">
        {rows.map((r) => (
          <li key={r.label} className="space-y-1.5">
            <div className="flex items-baseline justify-between gap-3 text-sm">
              <span className="truncate">{r.label}</span>
              <span className="tabular shrink-0">
                <span className="font-semibold">{count(r.kept)}</span>
                <span className="text-subtle">/{count(r.total)}</span>
              </span>
            </div>
            <Pips kept={r.kept} cancelled={r.total - r.kept} />
          </li>
        ))}
      </ul>
    </Surface>
  )
}

function CancelReasons({ orders }: { orders: DropOrder[] }) {
  const cancelled = orders.filter((o) => !isKept(o.status))
  const reasons = new Map<string, number>()
  for (const o of cancelled) {
    const r = o.cancelReason || 'No reason given'
    reasons.set(r, (reasons.get(r) ?? 0) + 1)
  }
  const rows = [...reasons].sort((a, b) => b[1] - a[1])
  return (
    <Surface>
      <PanelHeader title="Cancels" description={cancelled.length ? `${plural(cancelled.length, 'order')} taken back by the retailer` : undefined} />
      {rows.length === 0 ? (
        <p className="text-status-success-text px-5 pb-5 text-sm font-medium">Clean sweep. Nothing was cancelled.</p>
      ) : (
        <ul className="space-y-3 px-5 pb-5">
          {rows.map(([reason, n]) => (
            <li key={reason} className="space-y-1.5">
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className={cn('truncate', reason === 'No reason given' && 'text-muted-foreground')}>{reason}</span>
                <span className="tabular font-semibold">{count(n)}</span>
              </div>
              <div className="bg-surface-sunken h-1 overflow-hidden rounded-full" aria-hidden>
                <div className="bg-status-danger h-full rounded-full" style={{ width: `${(n / cancelled.length) * 100}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </Surface>
  )
}

const COLS = 'grid-cols-[minmax(0,1fr)_auto] lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)_9.5rem_5.5rem_1.25rem]'

function OrdersTable({ orders, retailer }: { orders: DropOrder[]; retailer: Retailer }) {
  const [show, setShow] = useState<'all' | 'kept' | 'cancelled'>('all')
  const keptCount = orders.filter((o) => isKept(o.status)).length
  const visible = useMemo(
    () => orders.filter((o) => show === 'all' || (show === 'kept') === isKept(o.status)),
    [orders, show],
  )
  return (
    <Surface aria-label="Orders in this drop">
      <PanelHeader
        title="Orders"
        description={`${plural(orders.length, 'order')} in this drop`}
        action={
          <ToggleGroup type="single" variant="outline" size="sm" value={show} onValueChange={(v) => v && setShow(v as typeof show)} aria-label="Show orders">
            <ToggleGroupItem value="all" className="px-3">All</ToggleGroupItem>
            <ToggleGroupItem value="kept" className="px-3">
              Went through <span className="tabular text-subtle">{count(keptCount)}</span>
            </ToggleGroupItem>
            <ToggleGroupItem value="cancelled" className="px-3">
              Cancelled <span className="tabular text-subtle">{count(orders.length - keptCount)}</span>
            </ToggleGroupItem>
          </ToggleGroup>
        }
      />
      <ColumnHeader cols={COLS} className="border-t">
        <span>Email</span>
        <span>Card</span>
        <span>Ship to</span>
        <span>Status</span>
        <span className="text-right">Total</span>
        <span />
      </ColumnHeader>
      {visible.length === 0 ? (
        <p className="text-muted-foreground border-t px-5 py-10 text-center text-sm">No orders in this view.</p>
      ) : (
        <ul className="border-t lg:border-t-0">
          {visible.map((o, i) => {
            const enter = rowEnter(i)
            return (
              <li key={o.orderNumber} className="border-b last:border-b-0">
                <Link
                  to="/orders/$orderNumber"
                  params={{ orderNumber: o.orderNumber }}
                  search={{ retailer }}
                  style={enter.style}
                  className={cn(rowClass, enter.className, COLS, 'py-3')}
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{o.email}</p>
                    <p className="text-muted-foreground id-mono truncate text-xs">
                      #{o.orderNumber}
                      {o.quantity > 1 && <span className="font-sans"> · {o.quantity} units</span>}
                    </p>
                  </div>
                  <p className="text-muted-foreground hidden truncate text-[13px] lg:block">
                    {o.cardType ?? '—'} {o.cardLastFour && <span className="tabular">·· {o.cardLastFour}</span>}
                  </p>
                  <p className="text-muted-foreground hidden truncate text-[13px] lg:block">
                    {o.address ? [[o.address.addressNumber, o.address.addressStreet].filter(Boolean).join(' '), o.address.city].filter(Boolean).join(', ') : '—'}
                  </p>
                  <div className="order-3 col-span-2 lg:order-none lg:col-span-1">
                    <StatusBadge status={o.status} />
                    {o.cancelReason && <p className="text-subtle mt-1 text-xs">{o.cancelReason}</p>}
                  </div>
                  <p className="tabular text-right text-sm font-medium">{money(o.total)}</p>
                  <RowChevron />
                </Link>
              </li>
            )
          })}
        </ul>
      )}
    </Surface>
  )
}
